/**
 * Tiny procedural SFX for gameplay feedback. We synthesize tones with Web
 * Audio rather than ship sample files — keeps the bundle small and avoids
 * dealing with asset pipelines. All calls no-op cleanly if audio context
 * creation fails (e.g. SSR, or browsers that block it).
 */

type Ctx = AudioContext;

export class Sfx {
  private ctx: Ctx | null = null;
  private masterGain: GainNode | null = null;
  private muted = false;

  ensureReady(): void {
    if (this.ctx) return;
    const Ctor = (globalThis.AudioContext ??
      (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return;
    try {
      this.ctx = new Ctor();
    } catch {
      return;
    }
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.4;
    this.masterGain.connect(this.ctx.destination);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain) this.masterGain.gain.value = muted ? 0 : 0.4;
  }

  peck(): void { this.beep(520, 0.07, "square", 0.18); }
  flap(): void { this.beep(180, 0.14, "triangle", 0.12); }
  shoo(): void { this.beep(260, 0.22, "sawtooth", 0.22); }
  gameOver(): void {
    this.beep(220, 0.18, "sawtooth", 0.25);
    setTimeout(() => this.beep(165, 0.3, "sawtooth", 0.3), 120);
  }
  pickup(): void { this.beep(880, 0.08, "sine", 0.16); }
  levelUp(): void {
    this.beep(660, 0.1, "triangle", 0.2);
    setTimeout(() => this.beep(880, 0.1, "triangle", 0.2), 90);
    setTimeout(() => this.beep(1320, 0.18, "triangle", 0.22), 180);
  }

  private beep(freq: number, duration: number, type: OscillatorType, peakGain: number): void {
    if (this.muted || !this.ctx || !this.masterGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peakGain, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }
}
